import { AbstractControl, ValidatorFn } from '@angular/forms';

/**
 * Creates a custom validator to compare the value of a FormControl against another.
 *
 * @param otherControlName The name of the control against which the current control's value will be compared.
 * @returns A validator function that Angular can use to determine whether the two controls' values match.
 */
export function matchOtherValidator(otherControlName: string): ValidatorFn {
  // The validator function that is returned and which Angular will call with the control to be validated.
  return (control: AbstractControl): { [key: string]: any } | null => {
    // Checks if the control has a parent FormGroup and exists; if not, returns null (does not apply validation).
    if (!control.parent || !control) {
      return null;
    }

    // Attempts to retrieve the control against which the current control's value is to be compared from the parent FormGroup.
    const otherControl = control.parent.get(otherControlName);
    // If the other control cannot be found, throws an error indicating it could not be found.
    if (!otherControl) {
      throw new Error('matchOtherValidator: other control is not found in parent form group');
    }

    // Compares whether the current control's value is equal to the value of the control it is being compared against.
    let areEqual = control.value === otherControl.value;
    if (typeof control.value === 'string' && typeof otherControl.value === 'string') {
      areEqual = control.value.toLowerCase() === otherControl.value.toLowerCase();
    }

    // If the values are equal, returns null (meaning the validation passed).
    // If they are not equal, returns an error object with a 'fieldsMismatch' key to indicate the validation failure.
    return areEqual ? null : { 'fieldsMismatch': true };
  };
}
